import pLimit from 'p-limit';
import { match, isType } from 'variant';
import { CliError, CliErrorCode } from '@wix/cli-error';
import type { AccountAuth, SiteAuth } from '../schemas.js';
import { isValidToken } from '../auth-data.js';
import type {
  UnifiedAuthStrategy,
  AuthStrategyState,
} from './auth-strategy-types.js';
import { apiKeyAuthStrategy } from './api-key-auth-strategy.js';
import {
  renewAccountAuth,
  getNonExpiredAccountAuth,
  loginWithDeviceCode,
  loginWithRefreshToken,
} from './account-auth-strategy.js';
import {
  ensureAuthenticatedToSite,
  AuthenticationResult,
} from './site-auth-strategy.js';

async function createAuthState({
  userInfo,
  ...initialTokenData
}: AccountAuth): Promise<AuthStrategyState> {
  const limiter = pLimit(1);
  let accountTokenData = { ...initialTokenData };
  const siteTokensDataCache = new Map<string, SiteAuth>();

  async function getOrEnsureSiteAuth(siteId: string): Promise<SiteAuth> {
    const cached = siteTokensDataCache.get(siteId);
    if (cached && isValidToken(cached)) {
      return cached;
    }

    const siteAuthResult = await ensureAuthenticatedToSite({
      siteId,
      accountRefreshToken: accountTokenData.refreshToken,
    });

    if (!isType(siteAuthResult, AuthenticationResult.Ok)) {
      throw new CliError({
        code: CliErrorCode.FailedToRenewAuthTokenRefreshTokenRevoked(),
        cause: siteAuthResult.cause,
      });
    }

    const siteTokenData = siteAuthResult.payload;
    siteTokensDataCache.set(siteId, siteTokenData);

    return siteTokenData;
  }

  return {
    getAccessToken: ({ siteId } = {}) => {
      return limiter(async () => {
        if (siteId) {
          return (await getOrEnsureSiteAuth(siteId)).accessToken;
        }

        if (isValidToken(accountTokenData)) {
          return accountTokenData.accessToken;
        }

        const renewTokenResult = await renewAccountAuth({
          ...accountTokenData,
          userInfo,
        });

        return match(renewTokenResult, {
          TokenRenewed: ({ payload: newTokenData }) => {
            accountTokenData = newTokenData;
            return accountTokenData.accessToken;
          },
          RefreshTokenRevoked: ({ cause }) => {
            throw new CliError({
              code: CliErrorCode.FailedToRenewAuthTokenRefreshTokenRevoked(),
              cause,
            });
          },
        });
      });
    },
    getRefreshToken: () => accountTokenData.refreshToken,
    userInfo,
  };
}

export const unifiedAuthStrategy: UnifiedAuthStrategy = {
  async isLoggedIn() {
    const apiKeyAuth = await apiKeyAuthStrategy.isLoggedIn();

    if (apiKeyAuth) {
      return apiKeyAuth;
    }

    const accountAuth = await getNonExpiredAccountAuth();

    if (!accountAuth) {
      return null;
    }

    return await createAuthState(accountAuth);
  },

  async login({ deviceCode, signal }) {
    const accountAuth = await loginWithDeviceCode(deviceCode, {
      signal,
    });

    return await createAuthState(accountAuth);
  },

  async loginWithRefreshToken({ refreshToken }) {
    const authData = await loginWithRefreshToken({ refreshToken });

    return await createAuthState(authData);
  },
};
